import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as patientActions from './patient.actions';
import {PatientService} from 'src/app/components/patient/services/patient.service';

import {of, tap} from 'rxjs';
import {catchError, filter, map, mergeMap, retry, switchMap} from 'rxjs/operators';
import {UNSELECT_AGENDA} from '@components/agenda/store/agenda.actions';
import {IntegralHealth} from '@components/patient/interfaces/family-doctor.interface';
import {FamilyDoctorService} from '@components/patient/services/family-doctor.service';
import {environment} from '@environments/environment';
import {REGEX_CLEAN_RUT} from '@shared/constants/regex.const';
import {ProfessionalRef} from '@auth/interfaces/professional.interface';

@Injectable()
export class PatientEffects {
  getPatient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.GET_PATIENT),
      switchMap(({docNumber, docType}) =>
        this.patientService.getPatient(docNumber, docType).pipe(
          map((patient) => patientActions.GET_PATIENT_OK({patient})),
          catchError((err) => of(patientActions.GET_PATIENT_ERR({payload: err}))),
        ),
      ),
    ),
  );

  getIntegralHealth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.GET_PATIENT_OK),
      filter(({patient}) => Boolean(patient)),
      filter(({patient}) => patient?.integralHealth === undefined),
      map(({patient}) => patientActions.GET_PATIENT_INTEGRAL_HEALTH({docNumber: patient.documentNumber})),
      catchError((err) => of(patientActions.GET_PATIENT_INTEGRAL_HEALTH_ERR({payload: err}))),
    ),
  );

  getMarkOnAgenda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.GET_PATIENT_OK),
      filter(({patient}) => Boolean(patient)),
      filter(({patient}) => patient?.marksOnAgenda === undefined),
      switchMap(({patient}) =>
        this.patientService.getMarkOnAgenda(patient.id).pipe(
          map((marksOnAgenda) => patientActions.GET_PATIENT_MARK_ON_AGENDA_OK({marksOnAgenda})),
          catchError((err) => of(patientActions.GET_PATIENT_MARK_ON_AGENDA_ERR({payload: err}))),
        ),
      ),
    ),
  );

  setIntegralHealth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.GET_PATIENT_INTEGRAL_HEALTH),
      switchMap(({docNumber}) => this.familyDoctorService.getPatientIH(docNumber)),
      map((integralHealth: IntegralHealth) => patientActions.GET_PATIENT_INTEGRAL_HEALTH_OK({integralHealth})),
      catchError((err) => of(patientActions.GET_PATIENT_INTEGRAL_HEALTH_ERR({payload: err}))),
    ),
  );

  getFamilyDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.GET_PATIENT_INTEGRAL_HEALTH_OK),
      switchMap(({integralHealth}) => this.familyDoctorService.getProfessional(integralHealth?.beneficiary[0]?.professionalGpId)),
      map((familyDoctor: ProfessionalRef) => patientActions.GET_FAMILY_DOCTOR_OK({familyDoctor})),
      catchError((err) => of(patientActions.GET_FAMILY_DOCTOR_ERR({payload: err}))),
    ),
  );

  clearPatientAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.CLEAR_PATIENT),
      map(() => UNSELECT_AGENDA()),
    ),
  );

  constructor(
    private actions$: Actions,
    private patientService: PatientService,
    private familyDoctorService: FamilyDoctorService,
  ) {
  }
}
